import dateUtils from 'app/shared/utils/dateUtils';
import { listingUtils_truncateListingUpdated } from 'app/shared/utils/listingUtils';

type DateAgoLike = {
  date?: unknown;
  ago?: unknown;
};

type ListingHistoryLike = {
  activated?: unknown;
  lastUpdated?: unknown;
};

export type TrustSignalDisplay = {
  listedText: string;
  listedIso: string;
  updatedText: string;
  updatedIso: string;
  showListed: boolean;
  showUpdated: boolean;
};

const EMPTY_DISPLAY: TrustSignalDisplay = {
  listedText: '',
  listedIso: '',
  updatedText: '',
  updatedIso: '',
  showListed: false,
  showUpdated: false,
};

const toDateAgo = (value: unknown): { date?: number; ago?: string } => {
  if (!value || typeof value !== 'object') return {};
  const { date, ago } = value as DateAgoLike;
  const validDate = typeof date === 'number' && Number.isFinite(date) && date > 0 ? date : undefined;
  const validAgo = typeof ago === 'string' && ago.trim().length > 0 ? ago : undefined;
  return { date: validDate, ago: validAgo };
};

const safeFormatMonthDay = (date: number): string => {
  try {
    const formatted = dateUtils.formatDateToString(date, { skipYear: true });
    return typeof formatted === 'string' ? formatted : '';
  } catch {
    return '';
  }
};

const toIsoString = (date: number): string => {
  try {
    const iso = new Date(date).toISOString();
    return iso;
  } catch {
    return '';
  }
};

/**
 * Derives the "Updated X ago" and "Listed <Month Day>" strings shown below the
 * photo gallery. Returns empty strings (and `show* = false`) for any missing,
 * malformed, or incomplete history field so the caller can render nothing.
 *
 * Mirrors legacy behavior: prefer lastUpdated for the "Updated" line, fall back
 * to activated when there is no update; show "Listed" only when `activated.date`
 * is a real timestamp.
 */
export const getTrustSignalDisplay = (history: ListingHistoryLike | null | undefined): TrustSignalDisplay => {
  if (!history || typeof history !== 'object') return EMPTY_DISPLAY;

  const activated = toDateAgo(history.activated);
  const lastUpdated = toDateAgo(history.lastUpdated);

  const updatedSource = lastUpdated.ago ? lastUpdated : activated;
  const truncatedAgo = updatedSource.ago ? listingUtils_truncateListingUpdated(updatedSource.ago) : '';
  const updatedText = truncatedAgo ? `Updated ${truncatedAgo}` : '';
  const updatedIso = updatedSource.date ? toIsoString(updatedSource.date) : '';

  const listedMonthDay = activated.date ? safeFormatMonthDay(activated.date) : '';
  const listedText = listedMonthDay ? `Listed ${listedMonthDay}` : '';
  const listedIso = activated.date ? toIsoString(activated.date) : '';

  return {
    listedText,
    listedIso,
    updatedText,
    updatedIso,
    showListed: Boolean(listedText),
    showUpdated: Boolean(updatedText),
  };
};
